import {
  DocumentSearchParams,
  SetSearchPayload,
} from 'components/documents-search/types';
import request from 'lib/request';
import { uniqBy } from 'lodash-es';
import {
  Map,
  Point as MapPoint,
  MapLayerMouseEvent,
  CirclePaint,
  Expression,
} from 'mapbox-gl';
import { useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { Point, ReferenceLocationBucket } from 'types/api-responses';
import WebMercatorViewport from 'viewport-mercator-project';
import { MapUserConfig, PopupState } from './types';
import { getMapDefaultConfig } from './atoms';

const getNewReferenceLocationIds = (
  newId: number,
  current?: DocumentSearchParams
): number[] => {
  const currentIds = current?.referenceLocationIds || [];
  return [...new Set([...currentIds, newId])];
};

const onReferenceLocationClick = (
  e: MapLayerMouseEvent,
  searchPayload: DocumentSearchParams,
  setSearchPayload: SetSearchPayload
) => {
  const validSelection = getPolygonLayerIds(e.target, e.point);
  if (!validSelection.length) return;

  const newId = Number(validSelection[0]);
  if (isReferenceLocationSelected(searchPayload, newId)) return;

  setSearchPayload((current) => ({
    ...current,
    referenceLocationIds: getNewReferenceLocationIds(newId, current),
  }));
};

const areReferenceLocationBucketsEqual = (
  current?: ReferenceLocationBucket[],
  newCollection?: ReferenceLocationBucket[]
) => {
  if (current?.length === newCollection?.length) {
    return (
      current?.every((element, index) => {
        if (element.id === newCollection?.[index].id) {
          return true;
        }

        return false;
      }) ?? false
    );
  }
  return false;
};

const useCalculateMapDimensions = ({
  points,
  containerRef,
}: {
  points?: [Point, Point];
  containerRef: React.MutableRefObject<null>;
}) => {
  const { height, width } = useResizeDetector({
    targetRef: containerRef,
    handleWidth: true,
    handleHeight: true,
    refreshMode: 'debounce',
    refreshRate: 0,
  });

  const mercator = new WebMercatorViewport({
    height: height ?? 999,
    width: width ?? 999,
  });

  const defaultViewport = {
    latitude: 39.8283,
    longitude: -98.5795,
    zoom: 4,
  };

  // If no valid points provided, return default US-centered viewport
  if (!points) {
    return defaultViewport;
  }

  // Check if bounds are valid
  const [point1, point2] = points;

  // Validate that all coordinates are valid numbers
  const hasValidCoords =
    point1?.length === 2 &&
    point2?.length === 2 &&
    typeof point1[0] === 'number' &&
    typeof point1[1] === 'number' &&
    typeof point2[0] === 'number' &&
    typeof point2[1] === 'number' &&
    !Number.isNaN(point1[0]) &&
    !Number.isNaN(point1[1]) &&
    !Number.isNaN(point2[0]) &&
    !Number.isNaN(point2[1]) &&
    Number.isFinite(point1[0]) &&
    Number.isFinite(point1[1]) &&
    Number.isFinite(point2[0]) &&
    Number.isFinite(point2[1]);

  if (!hasValidCoords) {
    return defaultViewport;
  }

  // Check if bounds are not identical points
  const isValidBounds = point1[0] !== point2[0] || point1[1] !== point2[1];

  if (!isValidBounds) {
    return defaultViewport;
  }

  // Try to fit bounds, fall back to default if it fails
  try {
    const fitted = mercator.fitBounds(points, {
      padding: 20,
    });
    const { latitude: lat, longitude: lng, zoom: zm } = fitted;
    // viewport-mercator-project can yield NaN for degenerate bounds / tiny viewports
    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      !Number.isFinite(zm)
    ) {
      return defaultViewport;
    }
    return fitted;
  } catch (error) {
    console.warn('Failed to fit bounds, using default viewport:', error);
    return defaultViewport;
  }
};

const isReferenceLocationSelected = (
  searchPayload: DocumentSearchParams,
  id: number
): boolean => {
  return Boolean(searchPayload?.referenceLocationIds?.includes(id));
};

interface HoverLocationProps {
  e: MapLayerMouseEvent;
  setPopup: React.Dispatch<PopupState>;
}

const getLayer = (
  map: Map,
  point: MapPoint,
  layer:
    | 'development-area-horizontal-fill'
    | 'development-area-vertical-fill'
    | 'development-area-field-fill'
    | 'basin-fill'
    | 'tier-fill'
    | 'package-assets-fill'
    | 'title-entity-assets-fill'
) => {
  return map.getLayer(layer)
    ? map.queryRenderedFeatures(point, {
        layers: [layer],
      })
    : [];
};

const getPolygonLayerIds = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('polygon-fill-'))
    .flatMap((el) => (typeof el.id === 'number' ? [el.id] : []));
};

const getDevAreas = (
  map: Map,
  point: MapPoint,
  layerType:
    | 'development-area-horizontal-fill'
    | 'development-area-vertical-fill'
    | 'development-area-field-fill'
) => {
  const devAreaFeatures = getLayer(map, point, layerType);

  const developmentAreasData: { name: string }[] = devAreaFeatures.map(
    (el) => ({ name: el.properties?.name })
  );
  return uniqBy(developmentAreasData, 'name');
};

const getWells = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('wells-'));
};

const getDevelopmentAreaScopedWells = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('development-area-scoped-wells'));
};

const getTypeCurveScopedWells = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('type-curve-scoped-wells'));
};

const getDcaWells = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('dca-wells-'));
};

const getTitleEntityAssets = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('title-entity-assets'));
};

const getTypeCurveAreas = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('type-curves'));
};

const getPipelines = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('pipelines-'));
};

const getDrillingRigs = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('drilling-rigs'));
};

const getPortfolioAssets = (map: Map, point: MapPoint) => {
  return map
    .queryRenderedFeatures(point)
    .filter((el) => el.layer.id.startsWith('portfolio-assets-'));
};

const getAreaHref = (name: string) => `/development_areas/${name}`;

const setPopupState = ({ e, setPopup }: HoverLocationProps) => {
  const map = e.target;
  const developmentAreasHorizontal = getDevAreas(
    map,
    e.point,
    'development-area-horizontal-fill'
  );
  const developmentAreasVertical = getDevAreas(
    map,
    e.point,
    'development-area-vertical-fill'
  );
  const developmentAreasField = getDevAreas(
    map,
    e.point,
    'development-area-field-fill'
  );
  const basinLayer = getLayer(map, e.point, 'basin-fill');
  const tierLayer = getLayer(map, e.point, 'tier-fill');
  const polygonIds = getPolygonLayerIds(map, e.point);
  const wells = getWells(map, e.point);

  const developmentAreaScopedWells = getDevelopmentAreaScopedWells(
    map,
    e.point
  );

  const typeCurveScopedWells = getTypeCurveScopedWells(map, e.point);

  const dcaWells = getDcaWells(map, e.point);

  const titleEntityAssets = getTitleEntityAssets(map, e.point);

  const typeCurveAreas = getTypeCurveAreas(map, e.point);

  const portfolioAssets = getPortfolioAssets(map, e.point);

  const baseState = {
    longitude: e.lngLat.lng,
    latitude: e.lngLat.lat,
    visible: true,
  };

  const basinTiersNames: string[] = basinLayer
    .concat(tierLayer)
    .map((el) => el.properties?.tier || el.properties?.gis_basin);

  if (titleEntityAssets.length) {
    const entityIds = titleEntityAssets
      .filter((feature) => feature.properties?.entity_id)
      .map((feature) => ({
        id: feature.properties?.entity_id,
        primary_name: feature.properties?.primary_name || 'Unknown',
        interest_type: feature.properties?.interest_type,
      }));

    setPopup({
      ...baseState,
      data: {
        type: 'title-entity-assets',
        value: {
          entityIds,
        },
      },
    });
    return;
  }

  if (portfolioAssets.length) {
    const feature = portfolioAssets[0];
    const p = feature.properties ?? {};
    setPopup({
      ...baseState,
      data: {
        type: 'portfolio-assets',
        value: {
          tractName: p.tract_name ?? '',
          tractId: p.tract_id ?? null,
          entityName: p.entity_name ?? '',
          interestType: p.interest_type ?? null,
          netAcres: p.net_acres ?? null,
          grossAcres: p.gross_acres ?? null,
          startDepth: p.start_depth ?? null,
          endDepth: p.end_depth ?? null,
        },
      },
    });
    return;
  }

  // Check for pipeline clicks
  const pipelines = getPipelines(map, e.point);
  if (pipelines.length) {
    const pipeline = pipelines[0]; // Get the first pipeline clicked
    const uspipId = pipeline.properties?.uspip_id;
    const pipelineName =
      pipeline.properties?.operator || pipeline.properties?.system;

    if (uspipId) {
      setPopup({
        ...baseState,
        data: {
          type: 'pipeline',
          value: {
            uspipId: uspipId,
            pipelineName: pipelineName,
          },
        },
      });
      return;
    }
  }

  const drillingRigs = getDrillingRigs(map, e.point);
  if (drillingRigs.length) {
    const drillingRig = drillingRigs[0];
    const drillingRigId = drillingRig.properties?.rig_id;
    const drillingRigName = drillingRig.properties?.rig_name;
    const drillingRigUwi = drillingRig.properties?.uwi;
    if (drillingRigId) {
      setPopup({
        ...baseState,
        data: {
          type: 'drilling-rig',
          value: {
            rigId: drillingRigId,
            rigName: drillingRigName,
            uwi: drillingRigUwi,
          },
        },
      });
    }
    return;
  }

  if (typeCurveAreas.length) {
    const areaName =
      typeCurveAreas[0]?.properties?.name || 'Unknown Type Curve Area';

    setPopup({
      ...baseState,
      data: {
        type: 'type-curve-area',
        value: {
          areaName,
          typeCurves: [],
        },
      },
    });
    return;
  }

  if (developmentAreaScopedWells.length) {
    const wellProperties: Extract<
      PopupState['data'],
      { type: 'development-area-scoped-wells' }
    >['value'] = developmentAreaScopedWells.map((scopedWellGeo) => {
      return {
        uwi: scopedWellGeo.properties?.uwi ?? '',
        name: scopedWellGeo.properties?.well_name ?? '',
        status: scopedWellGeo.properties?.status ?? '',
        type: scopedWellGeo.properties?.type ?? '',
      };
    });

    setPopup({
      ...baseState,
      data: { type: 'development-area-scoped-wells', value: wellProperties },
    });
    return;
  }

  if (typeCurveScopedWells.length) {
    const wellProperties: Extract<
      PopupState['data'],
      { type: 'type-curve-scoped-wells' }
    >['value'] = typeCurveScopedWells.map((scopedWellGeo) => {
      return {
        uwi: scopedWellGeo.properties?.uwi ?? '',
        name: scopedWellGeo.properties?.well_name ?? '',
        status: scopedWellGeo.properties?.status ?? '',
        type: scopedWellGeo.properties?.type ?? '',
      };
    });

    setPopup({
      ...baseState,
      data: { type: 'type-curve-scoped-wells', value: wellProperties },
    });
    return;
  }

  if (dcaWells.length) {
    const dcaWellsData = dcaWells.reduce(
      (acc, el) => {
        const wellId: string | undefined = el.properties?.uwi;
        if (wellId) {
          acc[wellId] = undefined;
        }
        return acc;
      },
      {} as Record<string, undefined>
    );
    setPopup({
      ...baseState,
      data: { type: 'dca-wells', value: dcaWellsData },
    });
    return;
  }

  if (wells?.length) {
    const wellsData = wells.reduce(
      (acc, el) => {
        const wellId: string | undefined = el.properties?.uwi;
        if (wellId) {
          acc[wellId] = undefined;
        }
        return acc;
      },
      {} as Record<string, undefined>
    );
    setPopup({
      ...baseState,
      data: { type: 'wells', value: wellsData },
    });
    return;
  }

  if (polygonIds.length) {
    setPopup({
      ...baseState,
      data: { type: 'reference-locations', value: polygonIds },
    });
    return;
  }

  if (developmentAreasHorizontal?.length) {
    setPopup({
      ...baseState,
      data: {
        type: 'development-areas',
        value: {
          devAreas: developmentAreasHorizontal,
          basinTier: basinTiersNames,
        },
      },
    });
    return;
  }
  if (developmentAreasVertical?.length) {
    setPopup({
      ...baseState,
      data: {
        type: 'development-areas',
        value: {
          devAreas: developmentAreasVertical,
          basinTier: basinTiersNames,
        },
      },
    });
    return;
  }

  if (developmentAreasField?.length) {
    setPopup({
      ...baseState,
      data: {
        type: 'development-areas',
        value: {
          devAreas: developmentAreasField,
          basinTier: basinTiersNames,
        },
      },
    });
    return;
  }

  if (basinLayer.length || tierLayer.length) {
    setPopup({
      ...baseState,
      data: {
        type: 'basin-tier',
        value: basinTiersNames,
      },
    });
    return;
  }

  setPopup({ latitude: 0, longitude: 0, visible: false });
};

const isLocationHovered = (state: PopupState, locationId: number) => {
  if (state.data?.type === 'reference-locations') {
    return state.data.value.includes(locationId);
  }
  return false;
};

const usePopup = () => {
  return useState<PopupState>({
    latitude: 0,
    longitude: 0,
    visible: false,
    pinned: false,
  });
};

const getQuarteredGeometry = async (
  referenceLocationId: number,
  quarterCalls: string[],
  href: string,
  abortController: AbortController
) => {
  return await request.post<
    { geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon },
    unknown
  >(
    href,
    {
      referenceLocationId,
      quarterCalls,
    },
    abortController
  );
};

const circlePaint = (color: string | Expression): CirclePaint => {
  return {
    'circle-color': color,
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 1, 10, 2, 15, 4],
  };
};

const textSizeInterpolate: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['zoom'],
  5,
  4,
  10,
  8,
  15,
  16,
];

// The vertical keys of the four Create New Well statuses, which #7660 added defaulting off
// like every vertical key. A config saved before them has none, so the merge below would hide
// a vertical authored well even on a map showing verticals. On for those configs only
// (INS-13300); a saved value, or a config with no saved options, is left as it is.
const PRE_PERMIT_VERTICAL_KEYS = [
  'verticalProposal',
  'verticalPlanned',
  'verticalPrePermit',
  'verticalApd',
] as const;

/**
 * @param {object | undefined} saved - A saved config's wellsV2 options.
 * @return {object} `true` for each pre-permit vertical key the saved options lack.
 */
const savedPrePermitVerticalDefaults = (
  saved: Partial<MapUserConfig['wellsV2']['options']> | undefined
): Partial<MapUserConfig['wellsV2']['options']> =>
  saved
    ? Object.fromEntries(
        PRE_PERMIT_VERTICAL_KEYS.filter((key) => !(key in saved)).map((key) => [
          key,
          true,
        ])
      )
    : {};

const normalizeMapConfig = (config: MapUserConfig) => {
  const updatedConfig = structuredClone(config);
  const { titleEntityAssets, pipelines, drillingRigs, wellsV2 } =
    getMapDefaultConfig();
  if (!updatedConfig.titleEntityAssets) {
    updatedConfig.titleEntityAssets = titleEntityAssets;
  }
  if (!updatedConfig.pipelines) {
    updatedConfig.pipelines = pipelines;
  } else {
    updatedConfig.pipelines = {
      ...pipelines,
      ...updatedConfig.pipelines,
      options: {
        ...pipelines.options,
        ...updatedConfig.pipelines.options,
      },
    };
  }
  if (!updatedConfig.drillingRigs) {
    updatedConfig.drillingRigs = drillingRigs;
  }
  /* A saved config predates any status added since it was written — its `options` simply
     has no key for one, which reads as `undefined`, i.e. hidden. Merging under the
     defaults means a new status arrives switched on, exactly as it would for someone
     with no saved config, instead of being invisible until they find the checkbox.
     Same nested shape as `pipelines` above: the saved value still wins per key. */
  updatedConfig.wellsV2 = {
    ...wellsV2,
    ...updatedConfig.wellsV2,
    options: {
      ...wellsV2.options,
      ...savedPrePermitVerticalDefaults(updatedConfig.wellsV2?.options),
      ...updatedConfig.wellsV2?.options,
    },
  };
  return updatedConfig;
};

export {
  areReferenceLocationBucketsEqual,
  circlePaint,
  getAreaHref,
  getDevAreas,
  getLayer,
  getPipelines,
  getQuarteredGeometry,
  getTypeCurveAreas,
  getTypeCurveScopedWells,
  isLocationHovered,
  isReferenceLocationSelected,
  onReferenceLocationClick,
  setPopupState,
  textSizeInterpolate,
  useCalculateMapDimensions,
  usePopup,
  normalizeMapConfig,
  getDcaWells,
  getDrillingRigs,
};
